/**
 * Paging arithmetic, kept apart from the control that draws it.
 *
 * Off-by-ones here are the kind that go unnoticed: a footer reading
 * "Showing 1–25 of 24" or a Next button that stays live on the last page are
 * both wrong in a way nobody reports, so the sums are tested rather than eyeballed.
 */

/** Total pages, never fewer than one — an empty list still has a page to show. */
export const pageCount = (total, rows) => {
  const size = Number(rows) || 0;
  if (size <= 0) return 1;
  return Math.max(1, Math.ceil((Number(total) || 0) / size));
};

/** Clamp a page into range, so a stale page number cannot show an empty table. */
export const clampPage = (page, total, rows) =>
  Math.min(Math.max(1, Number(page) || 1), pageCount(total, rows));

/**
 * What this page covers, 1-indexed and inclusive, plus the slice bounds.
 * `first` is 0 when there is nothing at all, so the caller can say "Nothing to
 * show" rather than "Showing 1–0 of 0".
 */
export const pageRange = (page, total, rows) => {
  const count = Number(total) || 0;
  const size = Number(rows) || 0;
  if (count === 0 || size <= 0) return { first: 0, last: 0, from: 0, to: 0, page: 1, pages: 1 };
  const p = clampPage(page, count, size);
  const from = (p - 1) * size;
  const to = Math.min(from + size, count);
  return { first: from + 1, last: to, from, to, page: p, pages: pageCount(count, size) };
};

/**
 * The page numbers to draw, with gaps elided.
 *
 * Eight pages fit; eighty do not, and a footer that wraps to three lines of
 * numbers is worse than one that says "…". First and last are always offered
 * because "jump to the end" is a real thing people want.
 */
export const pageItems = (page, pages, { window: win = 1 } = {}) => {
  const total = Math.max(1, Number(pages) || 1);
  const current = clampPage(page, total, 1) && Math.min(Math.max(1, page || 1), total);
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const out = [1];
  const start = Math.max(2, current - win);
  const end = Math.min(total - 1, current + win);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i += 1) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
};

/** The slice itself, so a caller never re-derives the bounds. */
export const pageSlice = (list = [], page, rows) => {
  const { from, to } = pageRange(page, list.length, rows);
  return list.slice(from, to);
};
